/** @format */

import { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';

export const REVENUE_COLORS = {
  Room: '#6B8FD4',
  Food: '#82B879',
  Beverage: '#E5B94C',
  'F&B Others': '#D96B6B',
  Spa: '#62B4CC',
  MOD: '#9A8FC4',
  OOD: '#7C9BC9',
};

const DEFAULT_REVENUE_COLORS = [
  '#6B8FD4',
  '#82B879',
  '#E5B94C',
  '#D96B6B',
  '#62B4CC',
  '#9A8FC4',
];

const DATA_NUMBER_FONT = '"Roboto Condensed", sans-serif';

function getPerformanceColor(percentage) {
  const value = Number(percentage);

  if (!Number.isFinite(value)) {
    return '#374151';
  }

  if (value < -5) {
    return 'red';
  }

  if (value >= 0) {
    return 'green';
  }

  return 'orange';
}

export { getPerformanceColor };

export function getResponsiveDonutSettings(
  width = 1024,
  totalRevenueText = ''
) {
  const viewportWidth = Number.isFinite(width) ? width : 1024;
  const valueLength = String(totalRevenueText).length;

  const getValueFontSize = (defaultSize) => {
    if (valueLength >= 12) return Math.max(defaultSize - 7, 12);
    if (valueLength >= 10) return Math.max(defaultSize - 5, 13);
    if (valueLength >= 8) return Math.max(defaultSize - 3, 14);

    return defaultSize;
  };

  const getInnerRadius = (defaultRadius) => {
    if (valueLength >= 12) return '58%';
    if (valueLength >= 10) return '55%';
    if (valueLength >= 8) return '53%';

    return defaultRadius;
  };

  if (viewportWidth <= 375) {
    return {
      height: 225,
      radiusInner: getInnerRadius('45%'),
      radiusOuter: '70%',
      label: 10,
      totalRevenue: getValueFontSize(17),
      labelLineLength: 7,
      labelLineLength2: 13,
      edgeDistance: 2,
    };
  }

  if (viewportWidth < 480) {
    return {
      height: 240,
      radiusInner: getInnerRadius('45%'),
      radiusOuter: '72%',
      label: 10,
      totalRevenue: getValueFontSize(18),
      labelLineLength: 8,
      labelLineLength2: 15,
      edgeDistance: 2,
    };
  }

  if (viewportWidth < 768) {
    return {
      height: 260,
      radiusInner: getInnerRadius('46%'),
      radiusOuter: '73%',
      label: 11,
      totalRevenue: getValueFontSize(20),
      labelLineLength: 10,
      labelLineLength2: 18,
      edgeDistance: 4,
    };
  }

  return {
    height: 320,
    radiusInner: getInnerRadius('48%'),
    radiusOuter: '72%',
    label: 14,
    totalRevenue: getValueFontSize(30),
    labelLineLength: 12,
    labelLineLength2: 24,
    edgeDistance: 20,
  };
}

export function getRevenueColor(title, index = 0) {
  return (
    REVENUE_COLORS[title] ||
    DEFAULT_REVENUE_COLORS[
      index % DEFAULT_REVENUE_COLORS.length
    ]
  );
}

function DonutCard({
  data = [],
  totalRevenue,
  totalRevenueData,
}) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [deviceWidth, setDeviceWidth] = useState(() =>
    typeof window === 'undefined' ? 1024 : window.innerWidth
  );

  useEffect(() => {
    const updateDeviceWidth = () => {
      setDeviceWidth(window.innerWidth);
    };

    window.addEventListener('resize', updateDeviceWidth);

    return () => {
      window.removeEventListener('resize', updateDeviceWidth);
    };
  }, []);

  const totalRevenueInThousands =
    Number(totalRevenue ?? 0) / 1000;

  const formattedTotalRevenue = (
    Number.isFinite(totalRevenueInThousands)
      ? totalRevenueInThousands
      : 0
  ).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const responsiveSettings = getResponsiveDonutSettings(
    deviceWidth,
    formattedTotalRevenue
  );

  const totalRevenueColor = getPerformanceColor(
    totalRevenueData?.budget_percentage
  );

  const seriesData = data.map((item, index) => {
    const value = Number(item?.value);
    const title = item?.title || '';

    return {
      value: Number.isFinite(value)
        ? Number((value / 1000).toFixed(0))
        : 0,

      name: title,

      itemStyle: {
        color: getRevenueColor(title, index),
      },
    };
  });

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }

    const chart = echarts.init(chartRef.current);

    chartInstanceRef.current = chart;

    const createOption = () => {
      return {
        animation: true,

        grid: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          containLabel: true,
        },

        legend: {
          show: false,
        },

        tooltip: {
          trigger: 'item',

          extraCssText:
            'z-index:99999 !important; pointer-events:auto !important; background:transparent !important; border:none !important; box-shadow:none !important; padding:0 !important;',

          formatter: function (params) {
            const value = Number(
              params?.value ?? 0
            ).toLocaleString('en-IN', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });

            const percent = Number(params?.percent ?? 0);

            return `
              <div 
                style="
                  z-index:10000;
                  background-color:#e8e4e4;
                  font-size:0.75rem;
                  padding:8px;
                  border-radius:8px;
                  color:black;
                  position:relative;
                "
              >
                <span>(Rev in 000)</span>
                <br/>
                <span style="font-size:0.85rem">
                  ${params?.name || ''}:
                  <span
                    style="
                      font-family:${DATA_NUMBER_FONT};
                      font-variant-numeric:tabular-nums;
                    "
                  >
                    ${value}
                    (
                    ${
                      Number.isFinite(percent)
                        ? percent.toFixed(1)
                        : '0.0'
                    }%
                    )
                  </span>
                </span>
              </div>
            `;
          },
        },

        graphic: [
          {
            type: 'text',

            left: 'center',
            top: '42%',

            style: {
              text: 'Total Rev',
              textAlign: 'center',
              textVerticalAlign: 'middle',

              fill: '#374151',

              fontSize: responsiveSettings.label,

              fontWeight: 400,
            },
          },

          {
            type: 'text',

            left: 'center',
            top: '50%',

            style: {
              text: formattedTotalRevenue,
              textAlign: 'center',
              textVerticalAlign: 'middle',

              fill: totalRevenueColor,

              fontSize: responsiveSettings.totalRevenue,

              fontWeight: 'bold',

              fontFamily: DATA_NUMBER_FONT,
            },
          },
        ],

        series: [
          {
            name: 'Revenue Distribution',

            type: 'pie',

            radius: [
              responsiveSettings.radiusInner,
              responsiveSettings.radiusOuter,
            ],

            center: ['50%', '50%'],

            avoidLabelOverlap: true,

            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2,
            },

            label: {
              show: true,

              position: 'outside',

              fontSize: responsiveSettings.label,

              fontFamily: DATA_NUMBER_FONT,

              fontVariant: 'tabular-nums',

              edgeDistance:
                responsiveSettings.edgeDistance,

              distanceToLabelLine: 2,

              formatter: function (params) {
                const percent = Number(params?.percent ?? 0);

                return Number.isFinite(percent)
                  ? `${percent.toFixed(1)}%`
                  : '0.0%';
              },
            },

            labelLine: {
              show: true,

              length:
                responsiveSettings.labelLineLength,

              length2:
                responsiveSettings.labelLineLength2,

              smooth: false,

              maxSurfaceAngle: 80,

              lineStyle: {
                width: 1,
                color: '#374151',
              },
            },

            data: seriesData,
          },
        ],

        labelLayout: {
          hideOverlap: false,

          moveOverlap: 'shiftY',
        },
      };
    };

    chart.setOption(createOption());

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver = null;

    if (
      typeof ResizeObserver !== 'undefined' &&
      chartRef.current
    ) {
      resizeObserver = new ResizeObserver(handleResize);

      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);

      resizeObserver?.disconnect();

      chart.dispose();

      chartInstanceRef.current = null;
    };
  }, [
    totalRevenue,
    totalRevenueData,
    deviceWidth,

    responsiveSettings.height,
    responsiveSettings.radiusInner,
    responsiveSettings.radiusOuter,
    responsiveSettings.label,
    responsiveSettings.totalRevenue,
    responsiveSettings.labelLineLength,
    responsiveSettings.labelLineLength2,
    responsiveSettings.edgeDistance,

    formattedTotalRevenue,
  ]);

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,

        margin: 0,
        padding: 0,

        height: `${responsiveSettings.height}px`,

        overflow: 'visible',

        boxSizing: 'border-box',
      }}
    />
  );
}

export default DonutCard;
